import { DispatchDate } from '@utils/dispatch';

import type { AccountGateway } from '../../types/Account';
import type { AuthGateway } from '../../types/Auth';
import type {
  GeneratedAddress,
  GeneratedCreateDrawEntryIntentMutation,
  GeneratedDrawEntryIntentUpdateResponse,
  GeneratedGetDrawByProductIdQuery,
  GeneratedGetDrawEntryIntentQuery,
  GeneratedGetDrawStatusByProductIDsQuery,
  GeneratedGetProductsQuery,
  GeneratedMoneyInput,
  GeneratedOrderItem,
  GeneratedPaymentMethod,
  GeneratedUpdateDrawEntryIntentMutation,
} from '../../types/generated';
import { CurrencyCode } from '../../types/Locations';
import { PaymentGateway } from '../../types/Payment';
import { Frame, FrameOption } from '../../types/Products';

export interface StripeAddressDetails {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country: string;
  };
  phone?: string | null;
}

type Draw = GeneratedGetDrawByProductIdQuery['getDrawByProductId'];

export type AvailableDraw = {
  draw: Draw;
  product: GeneratedGetProductsQuery['getProducts'][number];
};

export type DrawWithFrames = AvailableDraw & {
  purchaseDetailsEnriched?: boolean;
  frames?: FrameOption[];
  frameRecommendedTitle?: string | null;
  frameRecommendedLabel?: string | null;
};

export enum PaymentType {
  CARD = 'card',
  CRYPTO = 'crypto',
}

export type DrawByProductId =
  GeneratedGetDrawByProductIdQuery['getDrawByProductId'];

export interface PreAuthDrawController {
  loading: (drawId: string) => boolean;
  updating: boolean;
  bid: number | undefined;
  setBid: (bid: number | undefined) => void;
  draw?: DrawByProductId;
  setupController: (drawWithFramesForSetup: DrawWithFrames) => Promise<void>;
  setupComplete: boolean;
  setupSecret?: string;
  entryIntentResponse:
    | GeneratedDrawEntryIntentUpdateResponse
    | {
        intent:
          | NonNullable<
              GeneratedCreateDrawEntryIntentMutation['createDrawEntryIntent']
            >['intent']
          | undefined;
        pricing:
          | NonNullable<
              Required<GeneratedCreateDrawEntryIntentMutation>['createDrawEntryIntent']
            >['pricing']
          | undefined;
        readyToConfirm: boolean;
        cryptoComPaymentId?: string;
      };
  updateDrawEntryIntent: () => Promise<void>;

  errorMessage: string | true | undefined;

  setSelectedAddress: (addressId: string) => void;
  selectedAddress: string | undefined;
  addresses: GeneratedAddress[];
  refreshAddresses: (newSelectedAddressId: string | undefined) => Promise<void>;

  setSelectedPaymentMethodId: (paymentMethodId: string | null) => void;
  selectedPaymentMethodId: string | null | undefined;
  paymentMethods: GeneratedPaymentMethod[];

  selectedPaymentType: PaymentType | null;
  setSelectedPaymentType: (paymentType: PaymentType | null) => void;

  paymentErrorCode: string | null;
  setPaymentErrorCode: (code: string | null) => void;

  usingNewPaymentMethod: boolean;
  newPaymentMethodType: string | undefined;
  setNewPaymentMethodType: (type: string | undefined) => void;
  newPaymentMethodUsingShippingAddress: boolean;
  setUsingNewPaymentMethod: (using: boolean) => void;
  setNewPaymentMethodUsingShippingAddress: (using: boolean) => void;
  billingAddress: StripeAddressDetails | null;
  setBillingAddress: (billingAddress: StripeAddressDetails | null) => void;

  setOptionalItems: (items: GeneratedOrderItem[]) => void;
  optionalItems: GeneratedOrderItem[];
  customerSelectedFrame: Frame | null | undefined;
  setCustomerSelectedFrame: (frame: Frame | null) => void;
  dispatchDate: DispatchDate;
  activeDraw: DrawWithFrames | null;
  resetController: () => void;
  isPreOrderSelected: boolean;
  setIsPreOrderSelected: (isPreOrderSelected: boolean) => void;
  bidValidationError: string | null;
}

export interface DrawGateway {
  createDrawEntryIntent(
    drawId: string,
    currencyCode: CurrencyCode,
    shippingAddressId?: string | null,
    paymentMethodId?: string | null,
    optionalItems?: GeneratedOrderItem[],
    cryptoPaymentRequested?: boolean | null,
    bid?: GeneratedMoneyInput,
  ): Promise<GeneratedCreateDrawEntryIntentMutation['createDrawEntryIntent']>;
  updateDrawEntryIntent(
    drawEntryIntentId: string,
    currencyCode: CurrencyCode,
    shippingAddressId?: string | null,
    paymentMethodId?: string | null,
    optionalItems?: GeneratedOrderItem[],
    cryptoPaymentRequested?: boolean | null,
    bid?: GeneratedMoneyInput,
  ): Promise<GeneratedUpdateDrawEntryIntentMutation['updateDrawEntryIntent']>;
  getDrawEntryIntent(
    drawEntryIntentId: string,
  ): Promise<GeneratedGetDrawEntryIntentQuery['getDrawEntryIntent']>;
  getDrawByProductId(productId: string): Promise<DrawByProductId | undefined>;
  getDrawStatusesByProductIds(
    productIds: string[],
  ): Promise<
    GeneratedGetDrawStatusByProductIDsQuery['getDrawByProductIDs'] | undefined
  >;
}

export interface PreAuthDrawOptions {
  product: ProductType;
  authGateway: AuthGateway;
  accountGateway: AccountGateway;
  paymentGateway: PaymentGateway;
}
